import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Chip,
  Stack,
  TextField,
  MenuItem,
  Tooltip,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountBalance as AccountIcon,
} from '@mui/icons-material';
import AccountService from '../../services/account.service';
import { usePagination } from '../../hooks/usePagination';

const AccountList: React.FC = () => {
  const navigate = useNavigate();
  const { pagination, setPagination, handlePageChange, handlePageSizeChange } = usePagination(10);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState<string>('accountNumber');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    fetchAccounts();
  }, [pagination.page, pagination.pageSize, typeFilter, statusFilter, searchTerm, sortField, sortOrder]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const params: any = {
        populate: 'parentAccount',
        'pagination[page]': pagination.page + 1, // Strapi uses 1-based indexing
        'pagination[pageSize]': pagination.pageSize,
        sort: `${sortField}:${sortOrder}`,
      };

      if (typeFilter !== 'all') {
        params['filters[accountType][$eq]'] = typeFilter;
      }

      if (statusFilter !== 'all') {
        params['filters[isActive][$eq]'] = statusFilter === 'active';
      }

      if (searchTerm) {
        params['filters[$or][0][accountNumber][$containsi]'] = searchTerm;
        params['filters[$or][1][accountName][$containsi]'] = searchTerm;
      }

      const response = await AccountService.getAll(params);
      const accountsData = response.data?.data || [];
      const paginationMeta = response.data?.meta?.pagination || [];
      
      setAccounts(accountsData);
      setPagination({
        total: paginationMeta.total || 0,
        pageCount: paginationMeta.pageCount || 0,
      });
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setAccounts([]);
      setPagination({ total: 0, pageCount: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this account?')) {
      try {
        await AccountService.delete(id);
        fetchAccounts();
      } catch (error) {
        console.error('Error deleting account:', error);
        alert('Cannot delete account. It may be in use.');
      }
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'asset':
        return 'success';
      case 'liability':
        return 'error';
      case 'equity':
        return 'primary';
      case 'revenue':
        return 'info';
      case 'expense':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    if (!amount) return 'OMR 0.00';
    return `OMR ${amount.toFixed(2)}`;
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <AccountIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Bank Accounts
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/accounts/create')}
        >
          New Account
        </Button>
      </Stack>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
          <TextField
            fullWidth
            label="Search..."
            variant="outlined"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              handlePageChange(0);
            }}
            placeholder="Search by account number or name"
          />
          <TextField
            select
            label="Account Type"
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              handlePageChange(0);
            }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">All Types</MenuItem>
            <MenuItem value="asset">Assets</MenuItem>
            <MenuItem value="liability">Liabilities</MenuItem>
            <MenuItem value="equity">Equity</MenuItem>
            <MenuItem value="revenue">Revenue</MenuItem>
            <MenuItem value="expense">Expenses</MenuItem>
          </TextField>
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              handlePageChange(0);
            }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Sort By</InputLabel>
            <Select
              value={`${sortField}:${sortOrder}`}
              label="Sort By"
              onChange={(e) => {
                const [field, order] = e.target.value.split(':');
                setSortField(field);
                setSortOrder(order as 'asc' | 'desc');
                handlePageChange(0);
              }}
            >
              <MenuItem value="accountNumber:asc">Account # (Ascending)</MenuItem>
              <MenuItem value="accountNumber:desc">Account # (Descending)</MenuItem>
              <MenuItem value="accountName:asc">Name (A-Z)</MenuItem>
              <MenuItem value="accountName:desc">Name (Z-A)</MenuItem>
              <MenuItem value="accountType:asc">Type (A-Z)</MenuItem>
              <MenuItem value="accountType:desc">Type (Z-A)</MenuItem>
              <MenuItem value="balance:desc">Balance (High to Low)</MenuItem>
              <MenuItem value="balance:asc">Balance (Low to High)</MenuItem>
              <MenuItem value="createdAt:desc">Created (Newest First)</MenuItem>
              <MenuItem value="createdAt:asc">Created (Oldest First)</MenuItem>
            </Select>
          </FormControl>
          {(searchTerm || typeFilter !== 'all' || statusFilter !== 'all') && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setSearchTerm('');
                setTypeFilter('all');
                setStatusFilter('all');
                handlePageChange(0);
              }}
              sx={{ minWidth: 120 }}
            >
              Clear Filters
            </Button>
          )}
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Account #</TableCell>
              <TableCell>Account Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Balance</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : accounts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No accounts found
                </TableCell>
              </TableRow>
            ) : (
              accounts.map((account) => {
                const attrs = account.attributes;

                return (
                  <TableRow key={account.id} hover>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {attrs?.accountNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1">
                        {attrs?.accountName}
                      </Typography>
                      {attrs?.parentAccount?.data && (
                        <Typography variant="caption" color="text.secondary">
                          Parent: {attrs.parentAccount.data.attributes?.accountName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={attrs?.accountType}
                        color={getTypeColor(attrs?.accountType)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        color={attrs?.balance >= 0 ? 'success.main' : 'error.main'}
                      >
                        {formatCurrency(attrs?.balance)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={attrs?.isActive ? 'Active' : 'Inactive'}
                        color={attrs?.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/accounts/${account.id}/edit`)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(account.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={pagination.total}
        rowsPerPage={pagination.pageSize}
        page={pagination.page}
        onPageChange={(_, newPage) => handlePageChange(newPage)}
        onRowsPerPageChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
      />
    </Box>
  );
};

export default AccountList;

